/**
 * Parc Trackers : les trackers comme équipement à part entière, indépendant du GRV auquel ils sont
 * éventuellement affectés (`docs/DECISIONS.md` D69). Il peut exister plus de trackers que de GRV —
 * en stock, disponibles, ou temporairement désassociés — d'où un module à part, pas un onglet du
 * Parc GRV.
 *
 * Même modèle que `EcranListeGrv.tsx` : tri et recherche côté client sur la liste entière, chargée
 * en une fois (le parc de trackers se compte en dizaines, pas en centaines).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import {
  EnTeteTriable,
  useTrie,
  useTriTableau,
} from "../../shared/composants/tableauTriable";
import {
  formaterDate,
  formaterDateSeule,
} from "../../shared/composants/formatage";
import { BoutonExporterCsv } from "../../shared/composants/exportCsv";
import type { Tracker } from "../../shared/api/types";
import { useTrackers } from "./requetes";

type Colonne =
  | "numero_serie"
  | "associe"
  | "numero_grv"
  | "associe_depuis"
  | "date_premiere_communication"
  | "derniere_communication"
  | "batterie_pct"
  | "position_gps"
  | "zone_nom";

type FiltreAssociation = "tous" | "associe" | "non_associe";

// Même format que `OngletMouvementsDetailles.tsx` : vide plutôt que "null, null" quand le tracker
// n'a encore jamais remonté de position.
function positionGpsDe(tracker: Tracker): string {
  return tracker.derniere_latitude != null && tracker.derniere_longitude != null
    ? `${tracker.derniere_latitude}, ${tracker.derniere_longitude}`
    : "";
}

function valeurDe(
  tracker: Tracker,
  colonne: Colonne,
): string | number | null | undefined {
  switch (colonne) {
    case "numero_serie":
      return tracker.numero_serie;
    case "associe":
      return tracker.associe ? 1 : 0;
    case "numero_grv":
      return tracker.numero_grv;
    case "associe_depuis":
      return tracker.associe_depuis;
    case "date_premiere_communication":
      return tracker.date_premiere_communication;
    case "derniere_communication":
      return tracker.derniere_communication;
    case "batterie_pct":
      return tracker.batterie_pct;
    case "position_gps":
      return positionGpsDe(tracker);
    case "zone_nom":
      return tracker.zone_nom;
  }
}

function texteRecherchable(tracker: Tracker): string {
  return [
    tracker.numero_serie,
    tracker.numero_grv,
    tracker.zone_nom,
    tracker.associe ? "associé" : "non associé",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function EcranParcTrackers() {
  const [recherche, definirRecherche] = useState("");
  const [filtreAssociation, definirFiltreAssociation] =
    useState<FiltreAssociation>("tous");
  const { tri, basculerTri } = useTriTableau<Colonne>("numero_serie");

  const requete = useTrackers();
  const trackers = useMemo(() => requete.data?.data ?? [], [requete.data]);

  const filtres = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return trackers.filter((tracker) => {
      if (filtreAssociation === "associe" && !tracker.associe) return false;
      if (filtreAssociation === "non_associe" && tracker.associe) return false;
      if (terme && !texteRecherchable(tracker).includes(terme)) return false;
      return true;
    });
  }, [trackers, recherche, filtreAssociation]);

  const triees = useTrie(filtres, tri, valeurDe);

  return (
    <section>
      <h1>Parc Trackers</h1>
      <p className="carte__description">
        Un tracker peut exister sans être affecté à un GRV — en stock,
        disponible, ou temporairement désassocié. L'affectation vient du
        référentiel de correspondance ; la communication et la batterie viennent
        de la dernière synchronisation Positrex.
      </p>

      <div className="barre-de-filtres">
        <label htmlFor="recherche-trackers">Rechercher</label>
        <input
          id="recherche-trackers"
          type="search"
          placeholder="numéro de série, numéro de GRV…"
          value={recherche}
          onChange={(evenement) => definirRecherche(evenement.target.value)}
        />

        <label htmlFor="filtre-association">Association</label>
        <select
          id="filtre-association"
          value={filtreAssociation}
          onChange={(evenement) =>
            definirFiltreAssociation(
              evenement.target.value as FiltreAssociation,
            )
          }
        >
          <option value="tous">Tous</option>
          <option value="associe">Associés</option>
          <option value="non_associe">Non associés</option>
        </select>

        <BoutonExporterCsv
          nomFichier="parc-trackers"
          colonnes={[
            {
              libelle: "Numéro de série",
              valeur: (t: Tracker) => t.numero_serie,
            },
            {
              libelle: "État",
              valeur: (t: Tracker) => (t.associe ? "Associé" : "Non associé"),
            },
            { libelle: "GRV associé", valeur: (t: Tracker) => t.numero_grv },
            {
              libelle: "Associé depuis",
              valeur: (t: Tracker) =>
                t.associe_depuis ? formaterDate(t.associe_depuis) : "",
            },
            {
              libelle: "Première communication",
              valeur: (t: Tracker) =>
                t.date_premiere_communication
                  ? formaterDateSeule(t.date_premiere_communication)
                  : "",
            },
            {
              libelle: "Dernière communication",
              valeur: (t: Tracker) =>
                t.derniere_communication
                  ? formaterDate(t.derniere_communication)
                  : "",
            },
            {
              libelle: "Batterie (%)",
              valeur: (t: Tracker) => t.batterie_pct ?? null,
            },
            {
              libelle: "Dernière position GPS",
              valeur: (t: Tracker) => positionGpsDe(t),
            },
            { libelle: "Géozone", valeur: (t: Tracker) => t.zone_nom ?? "" },
          ]}
          lignes={triees}
        />
      </div>

      {requete.isPending && (
        <Chargement libelle="Chargement du parc de trackers…" />
      )}
      {requete.isError && (
        <Erreur
          erreur={requete.error}
          reessayer={() => void requete.refetch()}
        />
      )}

      {requete.data && triees.length === 0 && (
        <Vide libelle="Aucun tracker ne correspond à ces critères." />
      )}

      {requete.data && triees.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <EnTeteTriable
                colonne="numero_serie"
                libelle="Numéro de série"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="associe"
                libelle="État"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="numero_grv"
                libelle="GRV associé"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="associe_depuis"
                libelle="Associé depuis"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="date_premiere_communication"
                libelle="Première communication"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="derniere_communication"
                libelle="Dernière communication"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="batterie_pct"
                libelle="Batterie"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="position_gps"
                libelle="Dernière position GPS"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="zone_nom"
                libelle="Géozone"
                tri={tri}
                basculerTri={basculerTri}
              />
            </tr>
          </thead>
          <tbody>
            {triees.map((tracker) => (
              <tr key={tracker.numero_serie}>
                <th scope="row">{tracker.numero_serie}</th>
                <td>
                  <span
                    className={
                      tracker.associe ? "badge badge--succes" : "badge"
                    }
                  >
                    {tracker.associe ? "Associé" : "Non associé"}
                  </span>
                </td>
                <td>
                  {tracker.numero_grv ? (
                    <Link to={`/grv/${encodeURIComponent(tracker.numero_grv)}`}>
                      {tracker.numero_grv}
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{formaterDate(tracker.associe_depuis)}</td>
                <td>
                  {formaterDateSeule(tracker.date_premiere_communication)}
                </td>
                <td>{formaterDate(tracker.derniere_communication)}</td>
                <td>
                  {tracker.batterie_pct === null ||
                  tracker.batterie_pct === undefined
                    ? "—"
                    : `${tracker.batterie_pct} %`}
                </td>
                <td>{positionGpsDe(tracker) || "—"}</td>
                <td>{tracker.zone_nom ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
